import { useEffect, useState, useCallback } from "react";
import { Grid2X2, Loader2, Trash2, Pencil, Hexagon, MapPin, Wand2 } from "lucide-react";
import { authFetch } from "@/lib/api";
import { useCultures, regionOfParcel } from "@/lib/cultures";
import { toast } from "sonner";
import IlotDrawModal from "./IlotDrawModal";
import IlotDetectModal from "./IlotDetectModal";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Sélecteur de culture : cultures de la parcelle, cultures connues de la région, saisie libre. */
function CultureSelect({ value, customValue, onValue, onCustom, cultures, known, testId }) {
  return (
    <>
      <select
        value={value}
        onChange={(e) => onValue(e.target.value)}
        className="h-8 text-xs border border-black/15 rounded-md px-1 bg-white"
        data-testid={testId}
      >
        <option value="">Culture…</option>
        {cultures.length > 0 && (
          <optgroup label="Cultures de la parcelle">
            {cultures.map((c) => <option key={`p-${c}`} value={c}>{c}</option>)}
          </optgroup>
        )}
        <optgroup label="Autres cultures">
          {known
            .filter((c) => !cultures.some((p) => p.toLowerCase() === c.toLowerCase()))
            .map((c) => <option key={`k-${c}`} value={c}>{c}</option>)}
        </optgroup>
        <option value="__autre__">Autre (saisie libre)…</option>
      </select>
      {value === "__autre__" && (
        <input
          value={customValue}
          onChange={(e) => onCustom(e.target.value)}
          placeholder="Nom de la culture"
          className="h-8 text-xs border border-black/15 rounded-md px-2 w-32"
          data-testid={`${testId}-free`}
        />
      )}
    </>
  );
}

/**
 * Sélecteur d'îlots (sous-parcelles) du Carnet PRO.
 * - « Parcelle entière » = vue globale (comportement historique).
 * - Un îlot sélectionné filtre les onglets et rattache les nouvelles saisies.
 * - Le « ＋ » ouvre la gestion : création, modification (crayon) et suppression.
 */
export default function IlotsBar({ parcel, cultures = [], selectedIlotId, onSelect, onIlotsChanged }) {
  const parcelId = parcel?.parcel_id;
  const [ilots, setIlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", culture: "", customCulture: "", surface_ha: "", points: null });
  // Cultures du référentiel de la région (en plus de celles de la parcelle)
  const known = useCultures(regionOfParcel(parcelId), { saisie: true });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", culture: "", customCulture: "", surface_ha: "", points: null });
  const [drawTarget, setDrawTarget] = useState(null); // "create" | ilot_id en cours d'édition
  const [detecting, setDetecting] = useState(false);


  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/ilots`);
      if (res.ok) {
        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];
        setIlots(arr);
        onIlotsChanged?.(arr);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelId]);

  useEffect(() => { load(); }, [load]);

  const parseHa = (v) =>
    v === "" || v === null || v === undefined ? null : parseFloat(String(v).replace(",", ".")) || null;

  const pointsFromGeometry = (g) => {
    const coords = g?.type === "Polygon" ? (g.coordinates?.[0] || []) : [];
    const pts = coords.map(([lon, lat]) => ({ lat, lon }));
    if (pts.length > 1 && pts[0].lat === pts[pts.length - 1].lat && pts[0].lon === pts[pts.length - 1].lon) pts.pop();
    return pts;
  };

  const createIlot = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Nom d'îlot requis"); return; }
    const culture = (form.culture === "__autre__" ? form.customCulture : form.culture).trim();
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/ilots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, culture, surface_ha: parseHa(form.surface_ha), points: form.points && form.points.length >= 3 ? form.points : null }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Îlot « ${name} » créé`);
        setForm({ name: "", culture: "", customCulture: "", surface_ha: "", points: null });
        await load();
        onSelect?.(data.ilot_id);
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Création impossible");
      }
    } catch { toast.error("Erreur de connexion"); }
    setBusy(false);
  };

  const startEdit = (ilot) => {
    const cult = ilot.culture || "";
    const inLists = !cult
      || cultures.some((c) => c.toLowerCase() === cult.toLowerCase())
      || known.some((c) => c.toLowerCase() === cult.toLowerCase());
    setEditingId(ilot.ilot_id);
    setEditForm({
      name: ilot.name || "",
      culture: inLists ? cult : "__autre__",
      customCulture: inLists ? "" : cult,
      surface_ha: ilot.surface_ha ?? "",
      points: ilot.geometry ? pointsFromGeometry(ilot.geometry) : null,
    });
  };

  const saveEdit = async () => {
    const name = editForm.name.trim();
    if (!name) { toast.error("Nom d'îlot requis"); return; }
    const culture = (editForm.culture === "__autre__" ? editForm.customCulture : editForm.culture).trim();
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/ilots/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, culture, surface_ha: parseHa(editForm.surface_ha), points: editForm.points && editForm.points.length >= 3 ? editForm.points : null }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Îlot modifié");
        setEditingId(null);
        await load();
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Modification impossible");
      }
    } catch { toast.error("Erreur de connexion"); }
    setBusy(false);
  };

  const removeIlot = async (ilot) => {
    if (!window.confirm(`Supprimer l'îlot « ${ilot.name} » ? Ses enregistrements repasseront en « parcelle entière ».`)) return;
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/ilots/${ilot.ilot_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Îlot supprimé");
        if (selectedIlotId === ilot.ilot_id) onSelect?.(null);
        if (editingId === ilot.ilot_id) setEditingId(null);
        await load();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(typeof d.detail === "string" ? d.detail : "Suppression impossible");
      }
    } catch { toast.error("Erreur de connexion"); }
  };

  const selected = ilots.find((i) => i.ilot_id === selectedIlotId) || null;

  return (
    <div className="shrink-0 border-b border-black/5" data-testid="ilots-bar">
      <div className="flex items-center gap-1.5 px-4 py-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold shrink-0" style={{ color: "#1A1A1A" }}>
          <Grid2X2 className="w-3.5 h-3.5" style={{ color: "#2f6a92" }} /> Îlots
        </span>
        <button
          onClick={() => onSelect?.(null)}
          className={`text-[10.5px] font-semibold rounded-full px-2.5 py-1 border ${!selectedIlotId ? "text-white border-transparent" : "border-black/15 hover:bg-black/5 text-gray-600"}`}
          style={!selectedIlotId ? { background: "#2f6a92" } : {}}
          data-testid="ilot-all"
        >
          Parcelle entière
        </button>
        {ilots.map((i) => (
          <button
            key={i.ilot_id}
            onClick={() => onSelect?.(i.ilot_id)}
            className={`text-[10.5px] font-semibold rounded-full px-2.5 py-1 border ${selectedIlotId === i.ilot_id ? "text-white border-transparent" : "border-black/15 hover:bg-black/5 text-gray-600"}`}
            style={selectedIlotId === i.ilot_id ? { background: "#2f6a92" } : {}}
            data-testid={`ilot-${i.ilot_id}`}
          >
            {i.geometry && <Hexagon className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />}
            {i.name}
            {i.culture ? <span className="font-medium opacity-75"> · {i.culture}</span> : null}
            {i.surface_ha ? <span className="font-medium opacity-75"> · {i.surface_ha} ha</span> : null}
          </button>
        ))}
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-[12px] font-bold border border-dashed border-black/25 rounded-full px-2.5 py-0.5"
          style={{ color: "#1f5c39" }}
          data-testid="ilots-manage"
          aria-label="Gérer les îlots"
        >
          {open ? "×" : "＋"}
        </button>
      </div>

      {selected && (
        <div className="px-4 py-1.5 text-[11px] font-semibold" style={{ background: "#dbe8f1", color: "#2f6a92" }} data-testid="ilot-context-note">
          Vous consultez « {selected.name} » — les onglets n'affichent que ses enregistrements, et toute nouvelle saisie lui sera rattachée.
          {" "}Calculs basés sur l'îlot : {selected.surface_ha ? `${selected.surface_ha} ha` : "surface de la parcelle (îlot sans surface)"}
          {selected.culture ? ` · ${selected.culture}` : ""}.
        </div>
      )}

      {open && (
        <div className="px-4 pb-3 pt-1 space-y-2">
          {/* Création */}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Nom (ex. Îlot 1)"
              className="h-8 text-xs border border-black/15 rounded-md px-2 w-32"
              data-testid="ilot-name-input"
            />
            <CultureSelect
              value={form.culture}
              customValue={form.customCulture}
              onValue={(v) => setForm((f) => ({ ...f, culture: v }))}
              onCustom={(v) => setForm((f) => ({ ...f, customCulture: v }))}
              cultures={cultures}
              known={known}
              testId="ilot-culture-select"
            />
            <input
              value={form.surface_ha}
              onChange={(e) => setForm((f) => ({ ...f, surface_ha: e.target.value }))}
              placeholder="Surface (ha)"
              inputMode="decimal"
              className="h-8 text-xs border border-black/15 rounded-md px-2 w-24"
              data-testid="ilot-surface-input"
            />
            <button
              onClick={() => setDrawTarget("create")}
              className="h-8 px-2.5 text-xs font-medium border border-black/15 rounded-md flex items-center gap-1"
              style={{ color: "#2f6a92" }}
              data-testid="ilot-draw-btn"
            >
              <MapPin className="w-3.5 h-3.5" />
              {form.points && form.points.length >= 3 ? `Tracé ✓ (${form.points.length} pts)` : "Tracer sur la carte"}
            </button>
            {parcel?.geometry && (
              <button
                onClick={() => setDetecting(true)}
                className="h-8 px-2.5 text-xs font-medium border border-black/15 rounded-md flex items-center gap-1"
                style={{ color: "#2f6a92" }}
                title="Repérer automatiquement les zones homogènes sur l'image satellite"
                data-testid="ilot-detect-btn"
              >
                <Wand2 className="w-3.5 h-3.5" /> Détecter les îlots
              </button>
            )}
            <button
              onClick={createIlot}
              disabled={busy}
              className="h-8 px-3 text-xs font-semibold rounded-md text-white"
              style={{ background: "#2D6A4F" }}
              data-testid="ilot-create-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Créer l'îlot"}
            </button>
          </div>

          {/* Liste : modifier / supprimer */}
          {ilots.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {ilots.map((i) => (
                editingId === i.ilot_id ? (
                  <div key={i.ilot_id} className="flex items-center gap-2 flex-wrap py-0.5" data-testid={`ilot-edit-row-${i.ilot_id}`}>
                    <input
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      className="h-8 text-xs border border-black/15 rounded-md px-2 w-32"
                      data-testid="ilot-edit-name"
                    />
                    <CultureSelect
                      value={editForm.culture}
                      customValue={editForm.customCulture}
                      onValue={(v) => setEditForm((f) => ({ ...f, culture: v }))}
                      onCustom={(v) => setEditForm((f) => ({ ...f, customCulture: v }))}
                      cultures={cultures}
                      known={known}
                      testId="ilot-edit-culture"
                    />
                    <input
                      value={editForm.surface_ha}
                      onChange={(e) => setEditForm((f) => ({ ...f, surface_ha: e.target.value }))}
                      placeholder="Surface (ha)"
                      inputMode="decimal"
                      className="h-8 text-xs border border-black/15 rounded-md px-2 w-24"
                      data-testid="ilot-edit-surface"
                    />
                    <button
                      onClick={() => setDrawTarget(i.ilot_id)}
                      className="h-8 px-2.5 text-xs font-medium border border-black/15 rounded-md flex items-center gap-1"
                      style={{ color: "#2f6a92" }}
                      data-testid="ilot-edit-draw"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      {editForm.points && editForm.points.length >= 3 ? `Tracé ✓ (${editForm.points.length} pts)` : "Tracer"}
                    </button>
                    <button onClick={saveEdit} disabled={busy}
                      className="h-8 px-3 text-xs font-semibold rounded-md text-white" style={{ background: "#2D6A4F" }} data-testid="ilot-edit-save">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Enregistrer"}
                    </button>
                    <button onClick={() => setEditingId(null)}
                      className="h-8 px-2.5 text-xs font-medium border border-black/15 rounded-md text-gray-600" data-testid="ilot-edit-cancel">
                      Annuler
                    </button>
                  </div>
                ) : (
                  <div key={i.ilot_id} className="flex items-center gap-2 text-[11.5px] text-gray-600">
                    <span className="font-semibold" style={{ color: "#1A1A1A" }}>{i.name}</span>
                    {i.culture && <span>· {i.culture}</span>}
                    {i.surface_ha ? <span>· {i.surface_ha} ha</span> : null}
                    <button
                      onClick={() => startEdit(i)}
                      className="ml-auto text-gray-400 hover:text-gray-700 p-0.5"
                      aria-label={`Modifier ${i.name}`}
                      data-testid={`ilot-edit-${i.ilot_id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeIlot(i)}
                      className="text-red-500 hover:text-red-700 p-0.5"
                      aria-label={`Supprimer ${i.name}`}
                      data-testid={`ilot-delete-${i.ilot_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      )}

      {detecting && (
        <IlotDetectModal
          parcel={parcel}
          existingNames={ilots.map((i) => i.name)}
          cultureOptions={[...new Set([...cultures, ...known].filter(Boolean))]}
          defaultCulture={cultures[0] || ""}
          onCreated={load}
          onClose={() => setDetecting(false)}
        />
      )}

      {drawTarget && (
        <IlotDrawModal
          parcel={parcel}
          otherIlots={ilots.filter((i) => drawTarget === "create" || i.ilot_id !== drawTarget)}
          initialPoints={drawTarget === "create" ? (form.points || []) : (editForm.points || [])}
          onDone={(pts) => {
            if (drawTarget === "create") setForm((f) => ({ ...f, points: pts }));
            else setEditForm((f) => ({ ...f, points: pts }));
            setDrawTarget(null);
          }}
          onClose={() => setDrawTarget(null)}
        />
      )}
    </div>
  );
}
